import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isCompanyName, taxBase } from "../../../../supabase/functions/_shared/taxpayer";
import { useApp } from "../context";
import type { CompanyCandidate, TaxpayerData } from "../data";
import { AiBadge } from "./AiBadge";
import { ErrorText } from "./ErrorText";

/** Whether the workshop's Számlázz.hu key is set (the NAV lookup goes through it). */
function useKeySet(): boolean | null {
  const { store, workshop } = useApp();
  const [set, setSet] = useState<boolean | null>(null);
  useEffect(() => {
    void store.invoiceKeySet(workshop.id).then(setSet, () => setSet(false));
  }, [store, workshop.id]);
  return set;
}

/**
 * "Adatok a NAV-tól" next to the tax number: NAV's name and seat address for it, through the
 * workshop's own Számlázz.hu key. Grey until a tax number is typed; without the key it says so,
 * and the form still works by hand.
 */
export function TaxpayerButton({ taxNumber, onFill }: { taxNumber: string | null | undefined; onFill: (d: TaxpayerData) => void }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const keySet = useKeySet();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [filled, setFilled] = useState<string | null>(null);
  const base = taxBase(taxNumber);
  const missing = keySet === false ? t("companyLookup.needsKey") : !base ? t("companyLookup.needsTaxNumber") : null;

  const run = async () => {
    setError(null);
    setFilled(null);
    setBusy(true);
    try {
      const d = await store.taxpayerLookup(workshop.id, taxNumber ?? "");
      onFill(d);
      setFilled(d.name);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="field company-nav">
      <button type="button" disabled={busy || !!missing || keySet === null} onClick={() => void run()}>
        {busy ? t("companyLookup.navBusy") : t("companyLookup.navButton")}
      </button>
      {missing && <span className="missing">{missing}</span>}
      {filled && <span className="ok" role="status">{t("companyLookup.navFilled", { name: filled })}</span>}
      <ErrorText error={error} />
    </div>
  );
}

/**
 * "Céges adatok keresése" for a company owner read from the registration: at most 3 candidates
 * as cards with a round selector. A NAV verified one is taken with one tap; a not verified one
 * needs one more tap to accept it. Private persons get nothing (no tax number).
 */
export function CompanyFinder({ name, city, onChoose }: { name: string; city?: string | null; onChoose: (c: CompanyCandidate | null) => void }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [found, setFound] = useState<{ candidates: CompanyCandidate[]; keySet: boolean } | null>(null);
  const [picked, setPicked] = useState<number | null>(null);
  const [accepted, setAccepted] = useState<number | null>(null);
  if (!isCompanyName(name)) return null;

  const search = async () => {
    setError(null);
    setFound(null);
    setPicked(null);
    setAccepted(null);
    onChoose(null);
    setBusy(true);
    try {
      setFound(await store.companyLookup(workshop.id, name, city ?? null));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const pick = (i: number) => {
    setPicked(i);
    const c = found!.candidates[i]!;
    if (c.verified) {
      setAccepted(i);
      onChoose(c);
    } else {
      setAccepted(null);
      onChoose(null);
    }
  };
  const accept = () => {
    if (picked === null) return;
    setAccepted(picked);
    onChoose(found!.candidates[picked]!);
  };
  const pickedCandidate = picked !== null ? found?.candidates[picked] : undefined;

  return (
    <div className="company-finder">
      <button type="button" className="primary" disabled={busy} onClick={() => void search()}>
        {busy ? t("companyLookup.searching") : t("companyLookup.search")}
      </button>
      <ErrorText error={error} />
      {found && (
        <>
          <p className="muted">{t("companyLookup.leasingNote")}</p>
          {!found.keySet && <p className="warn-text">{t("companyLookup.noKeyNote")}</p>}
          {found.candidates.length === 0 ? (
            <p role="status">{t("companyLookup.none")}</p>
          ) : (
            <fieldset className="choice-cards">
              <legend>{t("companyLookup.pick")}</legend>
              {found.candidates.map((c, i) => (
                <label key={c.taxNumber} className={`choice-card${picked === i ? " selected" : ""}`}>
                  <input type="radio" name="companyCandidate" checked={picked === i} onChange={() => pick(i)} />
                  <span>
                    <strong>{c.name}</strong>{" "}
                    {c.verified ? <span className="badge ok">{t("companyLookup.verified")}</span> : <span className="badge warn">{t("companyLookup.notVerified")}</span>}
                    <br />
                    {t("companyLookup.taxNumber", { taxNumber: c.taxNumber })}
                    <br />
                    <span className="muted">{[c.postalCode, c.city].filter(Boolean).join(" ")}{c.city && c.addressLine ? ", " : ""}{c.addressLine}</span>
                    {!c.verified && (
                      <>
                        <br />
                        <AiBadge />{" "}
                        {c.source && (
                          <a href={c.source} target="_blank" rel="noreferrer noopener">
                            {t("companyLookup.source")}
                          </a>
                        )}
                      </>
                    )}
                  </span>
                </label>
              ))}
            </fieldset>
          )}
          {pickedCandidate && !pickedCandidate.verified && accepted !== picked && (
            <div className="actions">
              <button type="button" onClick={accept}>
                {t("companyLookup.acceptUnverified")}
              </button>
            </div>
          )}
          {accepted !== null && found.candidates[accepted] && (
            <p className="ok" role="status">{t("companyLookup.chosen", { name: found.candidates[accepted]!.name })}</p>
          )}
        </>
      )}
    </div>
  );
}
